/**
 * Copyright Highway9 Networks Inc.
 */
import { useEffect, useMemo, useState } from 'react';
import { Typography, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Select, Input, Grid, Box, Accordion, SearchSelector } from '@h9/ui-lib';
import { authenticationService } from '~/services';
import { text } from '~/components/TextResources';
import { Icons } from '~/components/GraphicalResources';
import { ServiceInstanceRelease } from '~/types/SoftwareReleases';
import { GMapDialog, GMapStatic, type LocationObj } from '@h9/ui-lib';

import { useAppDispatch, useAppSelector } from '~/store/hooks';
import { tenantData } from '~/store/context/tenantSlice';
import { orderCreateActions } from '~/store/context/orderCreateSlice';
import { MAP_KEY } from '~/constants/constants';
import { deploymentCloudData, fetchVMCReleases, vmcReleasesData } from '~/store/context/serviceInstanceSlice';

export enum LOCATION_TYPE {
    GCP = "GCP",
    AWS = "AWS",
    AZURE = "AZURE",
    PRIVATE_CLOUD = "PRIVATE CLOUD"
}

type Props = {
}

const VMCController = (props: Props) => {
    const dispatch = useAppDispatch()
    const isOperator = authenticationService.isOperator()

    const vmcData = useAppSelector(state => state.orderCreate.orderConfig.vmc);


    const cloudClustersData = useAppSelector(deploymentCloudData);

    const tenantList = useAppSelector(tenantData);
    const releasesData = useAppSelector(vmcReleasesData)
    const { tenantId, serviceInstanceName, serviceInstanceDisplayName, release: selectedRelease, releaseTag: selectedLabel, serviceInstanceLocationType, deploymentCloudId, serviceInstanceLatitude, serviceInstanceLongitude, mcc, mnc, tac } = vmcData;

    const filteredCloudDeployData = useMemo(() => {
        return cloudClustersData.filter(item => item.location.locationType === serviceInstanceLocationType.replace(' ', '_') && (isOperator && item.isOperator))
    }, [cloudClustersData, serviceInstanceLocationType, isOperator])

    const [showLocationType, setShowLocationType] = useState(false)
    const [showDeploymentCloud, setShowDeploymentCloud] = useState(false)

    useEffect(() => {
        // set default deployment cloud id if available and show deployment cloud is true
        if (filteredCloudDeployData.length > 0 && showDeploymentCloud) {
            dispatch(orderCreateActions.setDeploymentCloudId(filteredCloudDeployData[0].id))
        }
    }, [dispatch, filteredCloudDeployData, showDeploymentCloud])
    useEffect(() => {
        dispatch(fetchVMCReleases({ filter: { tenantId: tenantId } }))
    }, [dispatch, tenantId])

    const filteredReleases = useMemo(() => tenantACLBasedFilter(releasesData, tenantId), [releasesData, tenantId]);

    const latestRelease = useMemo(() => findLatestVMCRelease(filteredReleases), [filteredReleases]);

    const releaseList = useMemo(() => (isOperator ? filteredReleases : latestRelease ? [latestRelease] : []), [isOperator, latestRelease, filteredReleases]);

    useEffect(() => {
        if (!latestRelease) return;

        const label = getPreferredReleaseLabel(latestRelease);
        dispatch(orderCreateActions.setRelease(latestRelease.name));
        if (label) {
            dispatch(orderCreateActions.setReleaseTag(label));
        }
    }, [dispatch, latestRelease]);

    const labelsData = useMemo(() => {
        const data = filteredReleases.find((item) => item.name === selectedRelease);
        return data?.labels ?? [];
    }, [filteredReleases, selectedRelease]);


    // Domain suffix from deployment cloud or current user
    const domainSuffix = useMemo(() => {
        if (deploymentCloudId) {
            const selectedCloud = filteredCloudDeployData.find(item => item.id === deploymentCloudId)
            return selectedCloud?.domainSuffix || authenticationService.currentUserValue.domainName
        }
        return authenticationService.currentUserValue.domainName
    }, [filteredCloudDeployData, deploymentCloudId])

    useEffect(() => {
        const keydownHandler = (e: KeyboardEvent) => {
            if (e.keyCode === 13 && e.ctrlKey) {
                console.log("Pressed control + Enter keys")
                setShowLocationType((prev) => !prev)
                setShowDeploymentCloud((prev) => !prev)
            }
        }
        document.addEventListener('keydown', keydownHandler)
        return () => {
            document.removeEventListener('keydown', keydownHandler)
        }
    }, [])

    const getLocationDetails = (locationObj: LocationObj) => {
        if (locationObj.lat === undefined || locationObj.lng === undefined) return;
        dispatch(orderCreateActions.setVMCLocations({ lat: locationObj.lat, lng: locationObj.lng }))
    }

    function handleTenantSelection(option: any) {
        const value = typeof option === 'string' ? option : option?.value;
        if (value) {
            dispatch(orderCreateActions.setTenantId(value))
        }
    }

    function handleServiceInstanceNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        const val = event.target.value.trim().toLowerCase();
        dispatch(orderCreateActions.setServiceInstanceName(val))
    }

    function handleServiceInstanceDisplayNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        const val = event.target.value;
        dispatch(orderCreateActions.setServiceInstanceDisplayName(val))
    }
    const handleMcc = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length < 4) {
            const value = e.target.value
            dispatch(orderCreateActions.setMcc(value))

        }

    }
    const handleMnc = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length < 4) {
            dispatch(orderCreateActions.setMnc(e.target.value))
        }
    }
    const handleTac = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value)
        if (value >= 1 && value <= 65535) {
            dispatch(orderCreateActions.setTac(value))
        }
    }
    function handleOnlyNumber(event: React.KeyboardEvent<HTMLInputElement>) {
        const ASCIICode = (event.which) ? event.which : event.keyCode
        if ((event?.key === '-' || event?.key === '+' || event?.key === '.' || event?.key.toLowerCase() === 'e' || (ASCIICode > 31 && (ASCIICode < 48 || ASCIICode > 57)))) {
            event.preventDefault();
        }

    }
    function handleServiceInstanceLocationTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
        dispatch(orderCreateActions.setServiceInstanceLocationType(event.target.value))
    }

    const handleReleaseChange = (option: any) => {
        const value = typeof option === 'string' ? option : option?.value;
        if (value) {
            dispatch(orderCreateActions.setRelease(value))

            const data = filteredReleases.find(item => item.name === value)
            if (data?.labels?.length) {
                dispatch(orderCreateActions.setReleaseTag(data.labels[0].name))
            }
        }
    }
    const handleLabelChange = (e: any) => {
        dispatch(orderCreateActions.setReleaseTag(e.target.value as string))
    }
    const handleDeploymentCloud = (e: any) => {
        dispatch(orderCreateActions.setDeploymentCloudId(e.target.value as string))

        const selectedCloud = filteredCloudDeployData.find(item => item.id === e.target.value)
        if (selectedCloud) {
            dispatch(orderCreateActions.setServiceInstanceLocation(selectedCloud.location.id))
            dispatch(orderCreateActions.setVMCLocations({
                lat: selectedCloud.location.latitude || 0,
                lng: selectedCloud.location.longitude || 0
            }))
        }
    }

    const selectedReleaseNotes = useMemo(() => {
        const data = filteredReleases.find((item) => item.name === selectedRelease);
        return data?.labels?.find((item) => item.name === selectedLabel)?.releaseNotes;
    }, [filteredReleases, selectedLabel, selectedRelease]);


    const hasServiceLocation = serviceInstanceLatitude !== undefined && serviceInstanceLongitude !== undefined;

    const tenantOptions = useMemo(() =>
        tenantList.map(tenant => ({
            label: tenant.name || '',
            value: tenant.id || '',
            icon: Icons.tenant,
        })).toSorted((a, b) => a.label.localeCompare(b.label))
        , [tenantList])

    const selectedTenantValue = useMemo(() => {
        if (!tenantId) return { label: '', value: '' };
        const tenant = tenantList.find(t => t.id === tenantId);
        return { label: tenant?.name || '', value: tenantId };
    }, [tenantId, tenantList])

    const releaseOptions = useMemo(() =>
        releaseList.map(item => ({
            label: `${item.name}`,
            value: item.name
        }))
        , [releaseList])

    const selectedReleaseValue = useMemo(() => {
        if (!selectedRelease) return { label: '', value: '' };
        return releaseOptions.find((option) => option.value === selectedRelease) ?? {
            label: selectedRelease,
            value: selectedRelease,
        };
    }, [releaseOptions, selectedRelease])

    const labelOptions = useMemo(() =>
        labelsData.map(item => ({
            id: item.name,
            label: item.name,
            value: item.name
        }))
        , [labelsData])

    const deploymentCloudOptions = useMemo(() =>
        filteredCloudDeployData.map(item => ({
            id: item.id,
            label: <>{Icons.resync}&nbsp;{item.name}</>,
            value: item.id
        }))
        , [filteredCloudDeployData])

    return (
        <Box sx={{ p: 4, border: '1px solid #e0e0e0', borderRadius: '5px', overflowY: 'auto', overflowX: 'hidden' }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: 16 }}>
                {text.controller}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                {text.formatString(text.controllerDesc!, text.private4GTitle!)}
            </Typography>
            <Grid container spacing={3}>
                <Grid display={"flex"} flexDirection={"column"} ml={1} gap={4} container size={{ xs: 12, lg: 5 }} spacing={1} >
                    {(authenticationService.isOperator() || authenticationService.isSystemEngineerOrMspAdmin()) && (
                        <FormControl sx={{ width: 400 }} variant="standard">
                            <SearchSelector
                                label={text.tenant}
                                options={tenantOptions}
                                value={selectedTenantValue}
                                startIcon={Icons.tenant}
                                onChange={handleTenantSelection}
                                variant="standard"
                            />
                        </FormControl>
                    )}
                    <Input
                        label={String(text.formatString(text.private4GTitleName!, text.serviceInstance!))}
                        type="text"
                        id="serviceInstanceName"
                        required

                        variant="standard"
                        value={serviceInstanceName}
                        onChange={handleServiceInstanceNameChange}
                        endAdornment={`.${domainSuffix}`}
                    />
                    <Input
                        label={text.displayName}
                        type="text"
                        id="serviceInstanceDisplayName"
                        required

                        variant="standard"
                        value={serviceInstanceDisplayName || ""}
                        onChange={handleServiceInstanceDisplayNameChange}
                    />
                    {showLocationType && (<>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{text.formatString(text.private4GCloudLocationType!, 'VMC')}</FormLabel>
                            <RadioGroup
                                row
                                aria-label="serviceInstanceLocationType"
                                name="serviceInstanceLocationType"
                                value={serviceInstanceLocationType}
                                onChange={handleServiceInstanceLocationTypeChange}
                            >
                                <FormControlLabel value={LOCATION_TYPE.GCP} control={<Radio />} label={text.google} />
                                <FormControlLabel value={LOCATION_TYPE.PRIVATE_CLOUD} control={<Radio />} label={text.ON_PREMISE} />
                                <FormControlLabel value={LOCATION_TYPE.AZURE} control={<Radio />} label={text.azure} />
                            </RadioGroup>
                        </FormControl>
                        {serviceInstanceLocationType === LOCATION_TYPE.PRIVATE_CLOUD && (
                            <GMapDialog
                                apiKey={MAP_KEY}
                                title="Select Location"
                                label="Select Location"
                                selectedLocation={hasServiceLocation ? { lat: serviceInstanceLatitude, lng: serviceInstanceLongitude } : undefined}
                                getLocationDetails={getLocationDetails}
                            />)}
                    </>)}
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }} ml={1}>
                    {hasServiceLocation && (
                        <Box sx={{ width: '100%', maxWidth: 800 }}>
                            <GMapStatic
                                apiKey={MAP_KEY}
                                lat={serviceInstanceLatitude!}
                                lng={serviceInstanceLongitude!}
                            />

                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                Latitude: {serviceInstanceLatitude} &nbsp; &nbsp;
                                Longitude: {serviceInstanceLongitude}
                            </Typography>
                        </Box>
                    )}
                </Grid>

            </Grid>
            <Accordion
                sx={{ mt: 2 }}
                title="Advanced"
                description={
                    <Grid container spacing={4} flexDirection={"row"}>
                        <Grid size={6}>
                            <Input
                                label={text.mcc}
                                type="string"
                                id="mcc"
                                inputProps={{ min: "0" }}
                                fullWidth
                                required
                                variant="standard"
                                onChange={handleMcc}
                                onKeyPress={handleOnlyNumber}
                                value={mcc || ""}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                label={text.mnc}
                                type="string"
                                inputProps={{ min: "0" }}
                                id="mnc"
                                fullWidth
                                required
                                variant="standard"
                                onChange={handleMnc}
                                onKeyPress={handleOnlyNumber}
                                value={mnc || ""}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                label={text.tac}
                                type="number"
                                inputProps={{ min: "0" }}
                                id="tac"
                                fullWidth
                                required
                                variant="standard"
                                onChange={handleTac}
                                onKeyPress={handleOnlyNumber}
                                value={tac || ""}
                            />
                        </Grid>
                        <Grid size={6} />
                        <Grid size={6}>
                            <FormControl fullWidth variant="standard">
                                <SearchSelector
                                    label="Release Number"
                                    options={releaseOptions}
                                    value={selectedReleaseValue}
                                    onChange={handleReleaseChange}
                                    variant="standard"
                                    style={{
                                        padding: "8px 14px",
                                        fontSize: "14px",
                                        border: "1px solid #ccc",
                                        backgroundColor: "transparent",
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={6}>
                            <FormControl fullWidth variant="standard">
                                <FormLabel component="legend">Release Type</FormLabel>
                                <Select
                                    id="Labels"
                                    value={selectedLabel || ''}
                                    onChange={handleLabelChange}
                                    options={labelOptions}
                                    sx={{ height: "32px" }}
                                />
                            </FormControl>
                        </Grid>
                        {showDeploymentCloud && authenticationService.isOperator() && (
                            <Grid size={6}>
                                <FormControl fullWidth sx={{ m: 1, mt: 0 }}>
                                    <FormLabel component="legend" sx={{ mb: 1 }}>{text.deploymentCluster}</FormLabel>
                                    <Select
                                        id="deploymentCloudId"
                                        variant="standard"
                                        value={deploymentCloudId || ''}
                                        onChange={handleDeploymentCloud}
                                        options={deploymentCloudOptions}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid size={12}>
                            <FormControl fullWidth variant="standard">
                                <FormLabel component="legend">Release Notes</FormLabel>
                                <Box sx={{ width: '100%', maxWidth: 800 }}>
                                    <textarea
                                        style={{
                                            width: '100%',
                                            minHeight: 200,
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontFamily: 'inherit',
                                            fontSize: '0.875rem',
                                            resize: 'vertical',
                                            overflow: 'auto',
                                            backgroundColor: '#f5f5f5',
                                            cursor: 'not-allowed',
                                            pointerEvents: 'none'
                                        }}
                                        value={selectedReleaseNotes || ""}
                                        readOnly
                                    />
                                </Box>
                            </FormControl>
                        </Grid>
                    </Grid>
                }
            />
        </Box>
    );
};

export default VMCController;

function getReleaseDate(release: ServiceInstanceRelease): number {
    const gaRecommended = release.labels?.find((label) => label.name === "GA" && label.recommended);
    const ga = release.labels?.find((label) => label.name === "GA");
    return gaRecommended?.date ?? ga?.date ?? release.labels?.[0]?.date ?? 0;
}

function findLatestVMCRelease(releases: ServiceInstanceRelease[]): ServiceInstanceRelease | undefined {
    if (!releases.length) return undefined;
    return releases.find((item) => item.labels?.some((label) => label.name === "GA")) ?? releases[0];
}

function getPreferredReleaseLabel(release: ServiceInstanceRelease): string | undefined {
    return (
        release.labels?.find((label) => label.name === "GA" && label.recommended)?.name ||
        release.labels?.find((label) => label.name === "GA")?.name ||
        release.labels?.[0]?.name
    );
}

function tenantACLBasedFilter(releasesData: ServiceInstanceRelease[], tenantId: string) {
    // if the release contains ACL visible to Partial , then filter the releases based on the tenant id and remove the releases which are not visible to the tenant id
    const _releases: ServiceInstanceRelease[] = [];
    for (let i = 0; i < releasesData.length; ++i) {
        const item = releasesData[i];
        if (item.acl?.visibility === "PARTIAL") {
            // console.log(item.acl.tenants, tenantId , 'xxx', item.name)
            const tenantIds = item.acl.tenants?.map(item => item.tenant.id) || [];
            if (tenantId && tenantIds.includes(tenantId)) {
                _releases.push(item);
            }
        } else {
            _releases.push(item);
        }
    }
    return _releases.toSorted((a, b) => getReleaseDate(b) - getReleaseDate(a));
}
